import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import {
  UsersIcon,
  ClipboardDocumentListIcon,
  DocumentIcon,
  KeyIcon,
  ShoppingCartIcon,
  CalendarIcon,
  GiftIcon,
  PlusIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/authStore';
import { useFamilyStore } from '../store/familyStore';
import { useTranslation } from 'react-i18next';
import api from '../api/client';

interface NotificationCounts {
  todos: number;
  documents: number;
  passwords: number;
  shopping: number;
  calendar: number;
  gifts: number;
  families: number;
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

export default function Dashboard() {
  const { user } = useAuthStore();
  const { families, fetchFamilies, currentFamily } = useFamilyStore();
  const { t } = useTranslation();
  
  // Fetch notification counts by category
  const { data: notificationCounts } = useQuery({
    queryKey: ['notificationCounts'],
    queryFn: async () => {
      const response = await api.get('/notifications/unread-by-category');
      return response.data as NotificationCounts;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const quickActions = [
    { key: 'todos', icon: ClipboardDocumentListIcon, label: t('dashboard.quickActions.todos'), path: '/todos', color: 'from-blue-500 to-cyan-500' },
    { key: 'documents', icon: DocumentIcon, label: t('dashboard.quickActions.documents'), path: '/documents', color: 'from-purple-500 to-pink-500' },
    { key: 'passwords', icon: KeyIcon, label: t('dashboard.quickActions.passwords'), path: '/passwords', color: 'from-amber-500 to-orange-500' },
    { key: 'shopping', icon: ShoppingCartIcon, label: t('dashboard.quickActions.shopping'), path: '/shopping', color: 'from-emerald-500 to-teal-500' },
    { key: 'calendar', icon: CalendarIcon, label: t('dashboard.quickActions.calendar'), path: '/calendar', color: 'from-rose-500 to-red-500' },
    { key: 'gifts', icon: GiftIcon, label: t('dashboard.quickActions.gifts', 'Gifts'), path: '/gifts', color: 'from-pink-500 to-rose-500' },
    { key: 'families', icon: UsersIcon, label: t('dashboard.quickActions.families'), path: '/families', color: 'from-indigo-500 to-violet-500' },
  ];
  
  useEffect(() => {
    fetchFamilies();
  }, [fetchFamilies]);

  useEffect(() => {
    const last = localStorage.getItem('greeting-last-shown');
    const today = new Date().toDateString();
    if (last !== today) {
      toast.success(greeting());
      localStorage.setItem('greeting-last-shown', today);
    }
  }, []);

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.greetingMorning');
    if (hour < 18) return t('dashboard.greetingAfternoon');
    return t('dashboard.greetingEvening');
  };

  return (
    <div className="page-content overflow-x-hidden">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-white mb-2">
          {greeting()}, {user?.first_name} 👋
        </h1>
        <p className="text-surface-400">
          {t('common.manageAccount')}
        </p>
      </motion.div>

      {/* Current Family Card */}
      {currentFamily ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-8 overflow-hidden relative hidden md:block"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-2xl">
                  🏠
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{currentFamily.name}</h2>
                  <p className="text-surface-400">
                    {t('families.membersCount', { count: currentFamily.members?.length || 0 })}
                  </p>
                </div>
              </div>
              <Link to={`/families/${currentFamily.id}`} className="btn-secondary">
                <span>{t('common.settings')}</span>
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      ) : families.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-8 text-center py-10"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center">
            <UsersIcon className="w-8 h-8 text-primary-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">{t('dashboard.noFamily')}</h3>
          <p className="text-surface-400 mb-6">
            {t('families.noneDesc')}
          </p>
          <Link to="/families" className="btn-primary inline-flex">
            <PlusIcon className="w-5 h-5" />
            <span>{t('dashboard.createFamilyCta')}</span>
          </Link>
        </motion.div>
      ) : null}

      {/* Quick Actions */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-3 gap-3 w-full"
      >
        {quickActions.map((action) => {
          const count = notificationCounts?.[action.key as keyof NotificationCounts] || 0;
          return (
            <motion.div key={action.path} variants={item} className="min-w-0">
              <Link
                to={action.path}
                className="card-interactive group relative flex flex-col items-center justify-center py-4 px-2 text-center w-full"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center transition-transform group-hover:scale-110 relative mb-2`}>
                  <action.icon className="w-8 h-8 text-white" />
                  {count > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg">
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </div>
                <span className="text-xs text-surface-300 font-medium truncate w-full">
                  {action.label}
                </span>
              </Link>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}
